import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Message, Loader } from "rsuite";
import { apiUrls } from "../../utils/apiUrls";
import { getData, postData } from "../../utils/reusables";
import { typeProviderNewsRun } from "../../utils/typeDefs";

/**
 * Trigger and status for the third-party news ingestion.
 *
 * Replaces the RegFollower RSS aggregator and shares nothing with it — that page and its endpoint
 * are expected to be deleted outright one day, and nothing here may depend on them.
 *
 * Written for an editor rather than a developer. The counts are labelled, the statuses say what
 * they mean, and the health banner answers the only question most visits are about: is this
 * still working. A scheduler that quietly stops firing leaves no other trace.
 */

const POLL_INTERVAL_MS = 2000;

/** How long the page keeps watching a running job. The job itself is never stopped by this. */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

type Health = {
  tone: "ok" | "bad" | "warn" | "running";
  headline: string;
  detail: string;
};

function formatDateForApi(value: Date | null): string {
  if (!value) return "";
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatClock(value: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function formatDay(value: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

/** How long the run took. Derived rather than stored, and useful for spotting one slowing down. */
function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return "—";

  const start = new Date(startedAt).getTime();
  const end = new Date(finishedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return "—";

  const seconds = (end - start) / 1000;
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;

  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

/** "4 minutes ago", "2 hours ago", "3 days ago" — how stale is this, at a glance. */
function timeAgo(value: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const minutes = Math.floor((Date.now() - parsed.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function describeHealth(run: typeProviderNewsRun | null): Health {
  if (!run) {
    return {
      tone: "warn",
      headline: "No news has been fetched yet",
      detail:
        "Nothing has run on this environment. Use Fetch now below to pull the first batch.",
    };
  }

  switch (run.status) {
    case "running":
      return {
        tone: "running",
        headline: "Fetching news now",
        detail: `Started ${timeAgo(run.startedAt)}. This page updates itself as it goes.`,
      };

    case "success":
      return {
        tone: run.tagsUnmapped > 0 ? "warn" : "ok",
        headline:
          run.tagsUnmapped > 0
            ? "Last fetch finished, but some news could not be placed"
            : "News is up to date",
        detail:
          run.tagsUnmapped > 0
            ? `Finished ${timeAgo(run.finishedAt)}. ${run.tagsUnmapped} tag(s) named a country or category we do not have, so that news is being held and is not visible to customers yet.`
            : `Last fetched ${timeAgo(run.finishedAt)} and everything was placed successfully.`,
      };

    case "failed":
      return {
        tone: "bad",
        headline: "The last fetch failed",
        detail: `It stopped ${timeAgo(run.finishedAt)}. Nothing new has come in since. The reason is shown below — if it is not clear, send it to a developer.`,
      };

    case "abandoned":
      return {
        tone: "bad",
        headline: "The last fetch never finished",
        detail: `It started ${timeAgo(run.startedAt)} and stopped part-way, usually because the server restarted or was deployed to. Nothing was damaged. Run it again with Fetch now.`,
      };

    default:
      return {
        tone: "warn",
        headline: `Last fetch reported "${run.status}"`,
        detail: `Started ${timeAgo(run.startedAt)}.`,
      };
  }
}

const STATUS_LEGEND: { label: string; colour: string; meaning: string }[] = [
  { label: "Up to date", colour: "#26a69a", meaning: "the last fetch finished and all news was placed" },
  { label: "Needs attention", colour: "#f5a623", meaning: "news arrived for a country or category we do not have" },
  { label: "Failed", colour: "#ef5350", meaning: "the fetch stopped with an error, or the server restarted mid-way" },
];

function Stat({
  value,
  label,
  hint,
  alert,
}: {
  value: number;
  label: string;
  hint: string;
  alert?: boolean;
}) {
  return (
    <div className={`pn-stat ${alert ? "pn-stat--alert" : ""}`} title={hint}>
      <div className="pn-stat-value">{value}</div>
      <div className="pn-stat-label">{label}</div>
    </div>
  );
}

function ProviderNews() {
  const [run, setRun] = useState<typeProviderNewsRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const [publishedFrom, setPublishedFrom] = useState<Date | null>(null);
  const [publishedTo, setPublishedTo] = useState<Date | null>(null);
  const [countries, setCountries] = useState("");

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartedAt = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const loadStatus = useCallback(async () => {
    const response = await getData(apiUrls.providerNews.status);
    const data = (response?.data?.data ?? null) as typeProviderNewsRun | null;
    setRun(data);
    return data;
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollStartedAt.current = Date.now();

    pollTimer.current = setInterval(async () => {
      const latest = await loadStatus();

      if (latest && latest.status !== "running") {
        stopPolling();
        return;
      }

      if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
        stopPolling();
        setMessage({
          text:
            "Still running after ten minutes. It has not been stopped — reload the page to check again.",
          ok: true,
        });
      }
    }, POLL_INTERVAL_MS);
  }, [loadStatus, stopPolling]);

  useEffect(() => {
    loadStatus()
      .then((latest) => {
        if (latest?.status === "running") startPolling();
      })
      .finally(() => setIsLoading(false));

    return stopPolling;
  }, [loadStatus, startPolling, stopPolling]);

  const handleFetch = async () => {
    setIsStarting(true);
    setMessage(null);

    const params = new URLSearchParams();
    const from = formatDateForApi(publishedFrom);
    const to = formatDateForApi(publishedTo);

    if (from) params.set("publishedFrom", from);
    if (to) params.set("publishedTo", to);
    if (countries.trim()) params.set("countries", countries.trim());
    params.set("triggeredBy", "manual");

    const response = await postData(
      `${apiUrls.providerNews.ingest}?${params.toString()}`,
      {},
    );

    setIsStarting(false);

    const succeeded = response?.data?.succeeded ?? false;
    setMessage({
      text:
        response?.data?.message ??
        "Could not reach the ingestion service. Check that the publisher API is running.",
      ok: succeeded,
    });

    if (succeeded) {
      await loadStatus();
      startPolling();
    }
  };

  const health = describeHealth(run);
  const isRunning = run?.status === "running";

  return (
    <div className="provider-news p-4">
      <h4 className="page-title mb-1">Provider News</h4>
      <p className="pn-page-subtitle mb-4">
        Third-party tax news syncs daily to the Information page by country and category. Use this screen to monitor status or trigger a manual fetch.
      </p>

      {isLoading ? (
        <Loader content="Checking the last fetch..." />
      ) : (
        <>
          <div className={`pn-health p-3 mb-3 pn-health--${health.tone}`}>
            <div className="pn-health-headline mb-1">{health.headline}</div>
            <div className="pn-health-detail">{health.detail}</div>

            {run && (
              <div className="pn-facts">
                <div>
                  <div className="pn-fact-label">Started</div>
                  <div className="pn-fact-value">{formatClock(run.startedAt)}</div>
                  <div className="pn-fact-sub">{formatDay(run.startedAt)}</div>
                </div>

                <div>
                  <div className="pn-fact-label">Finished</div>
                  <div className="pn-fact-value">
                    {run.finishedAt ? formatClock(run.finishedAt) : "Still running"}
                  </div>
                  <div className="pn-fact-sub">{formatDay(run.finishedAt)}</div>
                </div>

                <div>
                  <div className="pn-fact-label">Took</div>
                  <div className="pn-fact-value">
                    {formatDuration(run.startedAt, run.finishedAt)}
                  </div>
                </div>

                <div>
                  <div className="pn-fact-label">Started by</div>
                  <div className="pn-fact-value">
                    {run.triggeredBy === "manual" ? "By hand" : run.triggeredBy || "—"}
                  </div>
                  <div className="pn-fact-sub">
                    {run.triggeredBy === "manual" ? "from this screen" : "not from this screen"}
                  </div>
                </div>
              </div>
            )}

            {run?.errorMessage && (
              <Message
                className="mt-3"
                showIcon
                type={run.status === "success" ? "warning" : "error"}
                header="What the service reported"
              >
                {run.errorMessage}
              </Message>
            )}
          </div>

          {/* Sits immediately under the banner, because it explains the colour just seen there.
              Stacked rather than laid out in a row: three explanations do not fit across one
              line at most widths, and one of them ends up orphaned on its own. */}
          <div className="pn-legend d-flex flex-column gap-1 mb-3">
            {STATUS_LEGEND.map((entry) => (
              <span key={entry.label} className="d-inline-flex align-items-center gap-2">
                <span className="pn-legend-dot" style={{ background: entry.colour }} />
                <span>
                  <strong>{entry.label}</strong> — {entry.meaning}
                </span>
              </span>
            ))}
          </div>

          {run && (
            <div className="d-flex flex-wrap gap-2 mb-4">
              <Stat
                value={run.itemsReceived}
                label="News received"
                hint="How many news items the provider sent in the last fetch"
              />
              <Stat
                value={run.itemsInserted}
                label="New to us"
                hint="Items we had never seen before, now saved"
              />
              <Stat
                value={run.itemsUpdated}
                label="Updated"
                hint="Items the provider had edited since we last saw them"
              />
              <Stat
                value={run.tagsMapped}
                label="Placed on the grid"
                hint="Country and category slots filled — one news item can fill more than one"
              />
              <Stat
                value={run.tagsUnmapped}
                label="Could not be placed"
                alert={run.tagsUnmapped > 0}
                hint="News that named a country or category we do not have. It is saved but not shown to customers."
              />
            </div>
          )}

          <div className="pn-card p-3">
            <div className="pn-card-title mb-1">Fetch now</div>
            <p className="pn-health-detail mb-3">
              Use this when you do not want to wait for the daily run — for example after a
              category has been added, so news that could not be placed before can find its home.
              Running it again is always safe: anything already saved is left alone.
            </p>

            <div className="row g-3 mb-3" style={{ maxWidth: "48rem" }}>
              <div className="col-12 col-md-4">
                <div className="pn-field-label">Published from</div>
                <DatePicker
                  block
                  placeholder="Any date"
                  format="yyyy-MM-dd"
                  value={publishedFrom}
                  onChange={setPublishedFrom}
                  oneTap
                />
              </div>

              <div className="col-12 col-md-4">
                <div className="pn-field-label">Published to</div>
                <DatePicker
                  block
                  placeholder="Any date"
                  format="yyyy-MM-dd"
                  value={publishedTo}
                  onChange={setPublishedTo}
                  oneTap
                />
              </div>

              <div className="col-12 col-md-4">
                <div className="pn-field-label">Countries</div>
                <Input
                  placeholder="All countries"
                  value={countries}
                  onChange={setCountries}
                />
                <div className="pn-field-hint">Two-letter codes, e.g. AU, NZ</div>
              </div>
            </div>

            <p className="pn-field-hint mb-3">
              Leave all three empty to fetch everything the provider holds, which is the last three
              months for every country.
            </p>

            <Button
              className="primary-color"
              onClick={handleFetch}
              disabled={isStarting || isRunning}
              loading={isStarting}
            >
              {isRunning ? "Fetching..." : "Fetch now"}
            </Button>

            {message && (
              <Message className="mt-3" showIcon type={message.ok ? "success" : "error"}>
                {message.text}
              </Message>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ProviderNews;
